import { ref } from 'vue';

/**
 * Guards backdrop-to-dismiss so a modal only closes when the press AND the
 * release both land on the backdrop. A bare `@click.self` closes on any click
 * whose target is the backdrop — including the synthetic click the browser
 * fires when a text-selection drag *starts inside a field* and *ends on the
 * backdrop*, which silently discards typed data (CORA-264 #2).
 *
 * Apply both handlers to the backdrop element:
 *   <div @mousedown="onMousedown" @click="onClick"> … </div>
 * Each only acts when `event.target === event.currentTarget` (the backdrop
 * itself), so presses/clicks bubbling up from inner content are ignored.
 */
export function useBackdropDismiss(onDismiss: () => void) {
  // Whether the most recent mousedown originated on the backdrop itself.
  const pressedOnBackdrop = ref(false);

  const onMousedown = (event: MouseEvent) => {
    pressedOnBackdrop.value = event.target === event.currentTarget;
  };

  const onClick = (event: MouseEvent) => {
    // Close only when both ends of the gesture are the backdrop; reset so a
    // later stray click can't reuse a stale "pressed" flag.
    const releasedOnBackdrop = event.target === event.currentTarget;
    const shouldDismiss = pressedOnBackdrop.value && releasedOnBackdrop;
    pressedOnBackdrop.value = false;
    if (shouldDismiss) onDismiss();
  };

  return { onMousedown, onClick };
}
